/**
 * QueryBuilderDialog — Guided widget configuration dialog for creating
 * analytics widgets. Users select data sources, dimension, measure,
 * optional filters, chart type, and title. A live preview renders once
 * the minimum required fields are selected.
 *
 * Requirements: 4.1–4.11
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import type {
  ChartType,
  Dimension,
  Measure,
  QueryFilter,
  QueryResultRow,
} from '@coreflow/shared';
import {
  BOARD_COUNT_MAX,
  TITLE_MAX_LENGTH,
  validateTitle,
  validateBoardCount,
} from '@coreflow/shared';
import * as api from '../../services/api-client';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CHART_TYPES: { value: ChartType; label: string }[] = [
  { value: 'bar', label: 'Bar Chart' },
  { value: 'horizontal-bar', label: 'Horizontal Bar Chart' },
  { value: 'line', label: 'Line Chart' },
  { value: 'pie', label: 'Pie Chart' },
  { value: 'stacked-bar', label: 'Stacked Bar Chart' },
  { value: 'table', label: 'Data Table' },
  { value: 'burndown', label: 'Burndown' },
  { value: 'velocity', label: 'Velocity' },
  { value: 'cumulative-flow', label: 'Cumulative Flow Diagram' },
  { value: 'histogram', label: 'Histogram' },
  { value: 'wip-aging', label: 'WIP Aging' },
  { value: 'sprint-report', label: 'Sprint Report' },
];

const DIMENSION_OPTIONS: { value: Dimension; label: string }[] = [
  { value: 'list', label: 'List' },
  { value: 'swimlane', label: 'Swimlane' },
  { value: 'label', label: 'Label' },
  { value: 'assignee', label: 'Assignee' },
  { value: 'custom-field', label: 'Custom Field' },
  { value: 'due-date-week', label: 'Due Date (Week)' },
  { value: 'due-date-month', label: 'Due Date (Month)' },
  { value: 'board', label: 'Board' },
  { value: 'wip-status', label: 'WIP Status' },
  { value: 'sprint-field', label: 'Sprint Field' },
  { value: 'linked-to-list', label: 'Linked to List' },
];

const MEASURE_OPTIONS: { value: Measure['type']; label: string }[] = [
  { value: 'card-count', label: 'Card Count' },
  { value: 'numeric-field-sum', label: 'Numeric Field Sum' },
  { value: 'numeric-field-avg', label: 'Numeric Field Average' },
  { value: 'cycle-time-avg', label: 'Avg Cycle Time (Days)' },
  { value: 'lead-time-avg', label: 'Avg Lead Time (Days)' },
  { value: 'throughput', label: 'Throughput' },
  { value: 'card-age', label: 'Card Age (Days)' },
];

const FILTER_FIELDS: { value: QueryFilter['field']; label: string }[] = [
  { value: 'label', label: 'Label' },
  { value: 'assignee', label: 'Assignee' },
  { value: 'date-range', label: 'Date Range' },
  { value: 'list', label: 'List' },
  { value: 'swimlane', label: 'Swimlane' },
  { value: 'custom-field', label: 'Custom Field' },
];

const DEFAULT_TITLE = 'Untitled Widget';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface QueryBuilderDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly dashboardId: string;
  readonly onWidgetCreated: () => void;
}

interface BoardOption {
  readonly boardId: string;
  readonly title: string;
}

interface FormState {
  selectedBoardIds: string[];
  dimension: Dimension | '';
  measureType: Measure['type'] | '';
  measureFieldId: string;
  filters: QueryFilter[];
  chartType: ChartType | '';
  title: string;
}

interface FormErrors {
  boards?: string;
  dimension?: string;
  measure?: string;
  measureFieldId?: string;
  chartType?: string;
  title?: string;
}

// ---------------------------------------------------------------------------
// Initial State
// ---------------------------------------------------------------------------

const initialFormState: FormState = {
  selectedBoardIds: [],
  dimension: '',
  measureType: '',
  measureFieldId: '',
  filters: [],
  chartType: '',
  title: DEFAULT_TITLE,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function QueryBuilderDialog({
  open,
  onOpenChange,
  dashboardId,
  onWidgetCreated,
}: QueryBuilderDialogProps): React.ReactElement {
  const [form, setForm] = useState<FormState>(initialFormState);
  const [errors, setErrors] = useState<FormErrors>({});
  const [boards, setBoards] = useState<BoardOption[]>([]);
  const [boardsLoading, setBoardsLoading] = useState(false);
  const [previewData, setPreviewData] = useState<QueryResultRow[] | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // -------------------------------------------------------------------------
  // Fetch member boards on dialog open
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setBoardsLoading(true);

    async function loadBoards(): Promise<void> {
      try {
        const response = await api.get<{ data: BoardOption[] }>('/v1/boards');
        if (!cancelled) {
          const boardList = Array.isArray(response)
            ? response
            : Array.isArray((response as { data: BoardOption[] }).data)
              ? (response as { data: BoardOption[] }).data
              : [];
          setBoards(
            boardList.map((b: BoardOption) => ({ boardId: b.boardId, title: b.title })),
          );
        }
      } catch {
        if (!cancelled) {
          setBoards([]);
        }
      } finally {
        if (!cancelled) {
          setBoardsLoading(false);
        }
      }
    }

    loadBoards();
    return () => { cancelled = true; };
  }, [open]);

  // -------------------------------------------------------------------------
  // Reset state when dialog closes
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!open) {
      setForm(initialFormState);
      setErrors({});
      setPreviewData(null);
      setPreviewError(null);
      setSaveError(null);
    }
  }, [open]);

  // -------------------------------------------------------------------------
  // Live preview — trigger when all required fields are selected
  // -------------------------------------------------------------------------

  const canPreview = form.selectedBoardIds.length > 0 && form.dimension !== '' && form.measureType !== '';

  useEffect(() => {
    if (!canPreview) {
      setPreviewData(null);
      setPreviewError(null);
      return;
    }

    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);

    async function fetchPreview(): Promise<void> {
      try {
        const measure = buildMeasure(form.measureType as Measure['type'], form.measureFieldId);
        if (!measure) {
          if (!cancelled) {
            setPreviewData(null);
            setPreviewLoading(false);
          }
          return;
        }

        const payload = {
          boardIds: form.selectedBoardIds,
          dimension: form.dimension as Dimension,
          measure,
          filters: form.filters.length > 0 ? form.filters : undefined,
        };

        const result = await api.post<{ data: QueryResultRow[] }>(
          '/v1/analytics/query',
          payload,
        );
        if (!cancelled) {
          const rows = Array.isArray(result)
            ? result
            : Array.isArray((result as { data: QueryResultRow[] }).data)
              ? (result as { data: QueryResultRow[] }).data
              : [];
          setPreviewData(rows);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const message =
            err && typeof err === 'object' && 'message' in err
              ? (err as { message: string }).message
              : 'Preview failed';
          setPreviewError(message);
          setPreviewData(null);
        }
      } finally {
        if (!cancelled) {
          setPreviewLoading(false);
        }
      }
    }

    // Small debounce to avoid rapid fire requests
    const timer = setTimeout(fetchPreview, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [canPreview, form.selectedBoardIds, form.dimension, form.measureType, form.measureFieldId, form.filters]);

  // -------------------------------------------------------------------------
  // Form handlers
  // -------------------------------------------------------------------------

  const handleBoardToggle = useCallback((boardId: string, checked: boolean) => {
    setForm((prev) => {
      const next = checked
        ? [...prev.selectedBoardIds, boardId]
        : prev.selectedBoardIds.filter((id) => id !== boardId);

      // Enforce max 10
      if (next.length > BOARD_COUNT_MAX) return prev;
      return { ...prev, selectedBoardIds: next };
    });
    setErrors((prev) => ({ ...prev, boards: undefined }));
  }, []);

  const handleDimensionChange = useCallback((value: string) => {
    setForm((prev) => ({ ...prev, dimension: value as Dimension }));
    setErrors((prev) => ({ ...prev, dimension: undefined }));
  }, []);

  const handleMeasureTypeChange = useCallback((value: string) => {
    setForm((prev) => ({
      ...prev,
      measureType: value as Measure['type'],
      measureFieldId: value === 'card-count' ? '' : prev.measureFieldId,
    }));
    setErrors((prev) => ({ ...prev, measure: undefined, measureFieldId: undefined }));
  }, []);

  const handleMeasureFieldIdChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, measureFieldId: e.target.value }));
    setErrors((prev) => ({ ...prev, measureFieldId: undefined }));
  }, []);

  const handleChartTypeChange = useCallback((value: string) => {
    setForm((prev) => ({ ...prev, chartType: value as ChartType }));
    setErrors((prev) => ({ ...prev, chartType: undefined }));
  }, []);

  const handleTitleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, title: e.target.value }));
    setErrors((prev) => ({ ...prev, title: undefined }));
  }, []);

  // -------------------------------------------------------------------------
  // Filter management
  // -------------------------------------------------------------------------

  const handleAddFilter = useCallback((field: QueryFilter['field']) => {
    setForm((prev) => ({
      ...prev,
      filters: [
        ...prev.filters,
        { field, operator: 'in' as const, value: '' },
      ],
    }));
  }, []);

  const handleRemoveFilter = useCallback((index: number) => {
    setForm((prev) => ({
      ...prev,
      filters: prev.filters.filter((_, i) => i !== index),
    }));
  }, []);

  const handleFilterValueChange = useCallback((index: number, value: string) => {
    setForm((prev) => ({
      ...prev,
      filters: prev.filters.map((f, i) =>
        i === index ? { ...f, value } : f,
      ),
    }));
  }, []);

  // -------------------------------------------------------------------------
  // Validation
  // -------------------------------------------------------------------------

  const validate = useCallback((): FormErrors => {
    const errs: FormErrors = {};

    const boardValidation = validateBoardCount(form.selectedBoardIds.length);
    if (!boardValidation.valid) {
      errs.boards = boardValidation.error;
    }

    if (!form.dimension && form.chartType !== 'sprint-report') {
      errs.dimension = 'A dimension is required';
    }

    if (!form.measureType && form.chartType !== 'sprint-report') {
      errs.measure = 'A measure is required';
    }

    if (
      (form.measureType === 'numeric-field-sum' || form.measureType === 'numeric-field-avg') &&
      !form.measureFieldId.trim()
    ) {
      errs.measureFieldId = 'Field ID is required for numeric measures';
    }

    if (!form.chartType) {
      errs.chartType = 'A chart type is required';
    }

    const titleToValidate = form.title.trim() || DEFAULT_TITLE;
    const titleValidation = validateTitle(titleToValidate);
    if (!titleValidation.valid) {
      errs.title = titleValidation.error;
    }

    return errs;
  }, [form]);

  // -------------------------------------------------------------------------
  // Save handler
  // -------------------------------------------------------------------------

  const handleSave = useCallback(async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Sprint Report uses fixed dimension/measure — doesn't need user selection
    const isSprint = form.chartType === 'sprint-report';
    const measure = isSprint
      ? { type: 'card-count' as const }
      : buildMeasure(form.measureType as Measure['type'], form.measureFieldId);
    if (!measure) return;

    const widgetTitle = form.title.trim() || DEFAULT_TITLE;

    setIsSaving(true);
    setSaveError(null);

    try {
      await api.post(`/v1/analytics/dashboards/${dashboardId}/widgets`, {
        title: widgetTitle,
        chartType: form.chartType,
        dataSourceBoardIds: form.selectedBoardIds,
        dimension: isSprint ? 'swimlane' : form.dimension,
        measure,
        filters: form.filters.length > 0 ? form.filters : [],
      });
      onWidgetCreated();
      onOpenChange(false);
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? (err as { message: string }).message
          : 'Failed to save widget';
      setSaveError(message);
    } finally {
      setIsSaving(false);
    }
  }, [form, validate, dashboardId, onWidgetCreated, onOpenChange]);

  // -------------------------------------------------------------------------
  // Cancel handler
  // -------------------------------------------------------------------------

  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  const requiresFieldId = form.measureType === 'numeric-field-sum' || form.measureType === 'numeric-field-avg';

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby="query-builder-description">
        <DialogHeader>
          <DialogTitle>Add Widget</DialogTitle>
          <DialogDescription id="query-builder-description">
            Configure your widget by selecting data sources, dimensions, measures, and chart type.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* Step 1: Data Sources */}
          <fieldset>
            <legend className="mb-2 text-sm font-medium text-foreground">
              Data Sources (select up to {BOARD_COUNT_MAX} boards)
            </legend>
            {boardsLoading ? (
              <p className="text-sm text-muted-foreground" role="status">
                Loading boards…
              </p>
            ) : boards.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No boards available. You need board membership to add data sources.
              </p>
            ) : (
              <div className="max-h-40 overflow-y-auto rounded-md border border-input p-2 space-y-1" role="group" aria-label="Board selection">
                {boards.map((board) => (
                  <label
                    key={board.boardId}
                    className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-accent cursor-pointer"
                  >
                    <Checkbox
                      checked={form.selectedBoardIds.includes(board.boardId)}
                      onCheckedChange={(checked) =>
                        handleBoardToggle(board.boardId, checked === true)
                      }
                      disabled={
                        !form.selectedBoardIds.includes(board.boardId) &&
                        form.selectedBoardIds.length >= BOARD_COUNT_MAX
                      }
                      aria-label={`Select board: ${board.title}`}
                    />
                    <span className="truncate">{board.title}</span>
                  </label>
                ))}
              </div>
            )}
            {form.selectedBoardIds.length > 0 && (
              <p className="mt-1 text-xs text-muted-foreground">
                {form.selectedBoardIds.length}/{BOARD_COUNT_MAX} boards selected
              </p>
            )}
            {errors.boards && (
              <p className="mt-1 text-xs text-destructive" role="alert">{errors.boards}</p>
            )}
          </fieldset>

          {/* Step 2: Dimension */}
          <div className={`space-y-1 ${form.chartType === 'sprint-report' ? 'hidden' : ''}`}>
            <Label htmlFor="dimension-select">Dimension</Label>
            <Select value={form.dimension} onValueChange={handleDimensionChange}>
              <SelectTrigger id="dimension-select" aria-label="Select dimension">
                <SelectValue placeholder="Select a dimension…" />
              </SelectTrigger>
              <SelectContent>
                {DIMENSION_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.dimension && (
              <p className="text-xs text-destructive" role="alert">{errors.dimension}</p>
            )}
          </div>

          {/* Step 3: Measure */}
          <div className="space-y-1">
            <Label htmlFor="measure-select">Measure</Label>
            <Select value={form.measureType} onValueChange={handleMeasureTypeChange}>
              <SelectTrigger id="measure-select" aria-label="Select measure">
                <SelectValue placeholder="Select a measure…" />
              </SelectTrigger>
              <SelectContent>
                {MEASURE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.measure && (
              <p className="text-xs text-destructive" role="alert">{errors.measure}</p>
            )}

            {requiresFieldId && (
              <div className="mt-2 space-y-1">
                <Label htmlFor="measure-field-id">Field ID</Label>
                <Input
                  id="measure-field-id"
                  value={form.measureFieldId}
                  onChange={handleMeasureFieldIdChange}
                  placeholder="Enter the custom field ID"
                  aria-describedby={errors.measureFieldId ? 'measure-field-error' : undefined}
                  error={!!errors.measureFieldId}
                />
                {errors.measureFieldId && (
                  <p id="measure-field-error" className="text-xs text-destructive" role="alert">
                    {errors.measureFieldId}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Step 4: Optional Filters */}
          <div className="space-y-2">
            <Label>Filters (optional)</Label>
            {form.filters.length > 0 && (
              <div className="space-y-2">
                {form.filters.map((filter, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="text-xs font-medium text-muted-foreground min-w-[80px] capitalize">
                      {filter.field.replace('-', ' ')}
                    </span>
                    <Input
                      value={typeof filter.value === 'string' ? filter.value : ''}
                      onChange={(e) => handleFilterValueChange(index, e.target.value)}
                      placeholder="Filter value"
                      className="flex-1"
                      aria-label={`${filter.field} filter value`}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveFilter(index)}
                      aria-label={`Remove ${filter.field} filter`}
                    >
                      ✕
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-1">
              {FILTER_FIELDS.map((ff) => (
                <Button
                  key={ff.value}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleAddFilter(ff.value)}
                  className="text-xs"
                >
                  + {ff.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Step 5: Chart Type */}
          <div className="space-y-1">
            <Label htmlFor="chart-type-select">Chart Type</Label>
            <Select value={form.chartType} onValueChange={handleChartTypeChange}>
              <SelectTrigger id="chart-type-select" aria-label="Select chart type">
                <SelectValue placeholder="Select a chart type…" />
              </SelectTrigger>
              <SelectContent>
                {CHART_TYPES.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.chartType && (
              <p className="text-xs text-destructive" role="alert">{errors.chartType}</p>
            )}
          </div>

          {/* Step 6: Widget Title */}
          <div className="space-y-1">
            <Label htmlFor="widget-title">Widget Title</Label>
            <Input
              id="widget-title"
              value={form.title}
              onChange={handleTitleChange}
              maxLength={TITLE_MAX_LENGTH}
              placeholder={DEFAULT_TITLE}
              aria-describedby={errors.title ? 'widget-title-error' : 'widget-title-hint'}
              error={!!errors.title}
            />
            {errors.title ? (
              <p id="widget-title-error" className="text-xs text-destructive" role="alert">
                {errors.title}
              </p>
            ) : (
              <p id="widget-title-hint" className="text-xs text-muted-foreground">
                {form.title.trim().length}/{TITLE_MAX_LENGTH} characters
              </p>
            )}
          </div>

          {/* Live Preview */}
          {canPreview && (
            <section aria-label="Widget preview" className="rounded-md border border-input p-4">
              <h3 className="mb-2 text-sm font-medium text-foreground">Preview</h3>
              {previewLoading ? (
                <div className="flex items-center gap-2 py-4" role="status" aria-label="Loading preview">
                  <div
                    className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"
                    aria-hidden="true"
                  />
                  <span className="text-sm text-muted-foreground">Loading preview…</span>
                </div>
              ) : previewError ? (
                <p className="text-sm text-destructive" role="alert">{previewError}</p>
              ) : previewData && previewData.length === 0 ? (
                <p className="text-sm text-muted-foreground">No data available for the selected configuration.</p>
              ) : previewData ? (
                <div className="max-h-48 overflow-y-auto">
                  <table className="w-full text-sm" aria-label="Preview data">
                    <thead>
                      <tr className="border-b">
                        <th className="py-1 text-left font-medium text-muted-foreground" scope="col">
                          Dimension
                        </th>
                        <th className="py-1 text-right font-medium text-muted-foreground" scope="col">
                          Measure
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.slice(0, 10).map((row, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-1 text-foreground">{row.dimensionValue}</td>
                          <td className="py-1 text-right text-foreground">{row.measureValue}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {previewData.length > 10 && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      Showing 10 of {previewData.length} rows
                    </p>
                  )}
                </div>
              ) : null}
            </section>
          )}

          {/* Save Error */}
          {saveError && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3" role="alert">
              <p className="text-sm text-destructive">{saveError}</p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={handleCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving…' : 'Save Widget'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildMeasure(type: Measure['type'], fieldId: string): Measure | null {
  switch (type) {
    case 'card-count':
      return { type: 'card-count' };
    case 'numeric-field-sum':
      return fieldId.trim() ? { type: 'numeric-field-sum', fieldId: fieldId.trim() } : null;
    case 'numeric-field-avg':
      return fieldId.trim() ? { type: 'numeric-field-avg', fieldId: fieldId.trim() } : null;
    default:
      return null;
  }
}
